import type { Capability, ServerListItem } from '@/types/fleet';

/**
 * Planned launch fleet — the boxes in
 * planet-proxy-infra/docs/runbooks/launch-fleet-procurement.md.
 *
 * Organised by *purpose tier* rather than just region: the procurement plan
 * is 14 general VPN exits + 3 P2P exits + 1 Tor exit + (eventually) mixnet.
 * Specialised tiers live in DMCA / Tor / privacy-tolerant jurisdictions
 * that don't map to user-facing regions — they're declared separately so
 * the admin Regions tab can show buildout against each tier.
 *
 * Source of truth for the admin Regions tab. Keep this list in sync with
 * the procurement doc.
 */

export type PurposeTier = 'general' | 'p2p' | 'tor' | 'mixnet';

export interface PlannedExit {
  /** Stable key for React keys + matching; not user-visible. */
  id: string;
  /** Which tier this belongs to — drives both match logic and form defaults. */
  purpose: PurposeTier;
  /** ISO 3166-1 alpha-2 country code as it appears in `vps_servers.region_country`. */
  country: string;
  /**
   * City fragment to match against `vps_servers.region_city` —
   * case-insensitive substring match. Used when a single country has
   * multiple planned slots in the same tier (US East vs US West).
   */
  city?: string;
  /** Display label for the dashboard. */
  label: string;
  /** Provider we plan to order this from (operator hint). */
  providerHint: string;
  /** Continent name as the API stores it (e.g. "Europe", "North America"). */
  continent: string;
  /** City to pre-fill on the create-server form. */
  cityDefault: string;
  /** Provider slug to pre-fill — matches CreateServerBody.provider values. */
  providerSlug: string;
  /** Server-name template; `{N}` → zero-padded counter (01, 02…). */
  nameTemplate: string;
  /**
   * Capability the box MUST advertise to count toward this tier's coverage.
   * Absent for `general` (any active exit_node/vpn_server in the right
   * country qualifies). Set for `p2p`/`tor`/`mixnet`.
   */
  requiredCapability?: Capability;
}

export const PLANNED_EXITS: PlannedExit[] = [
  // ── General VPN exits — 14 regions ────────────────────────────────────
  { id: 'us-east', purpose: 'general', country: 'US', city: 'Ashburn',    cityDefault: 'Ashburn',     continent: 'North America', label: 'US East (Ashburn)',       providerHint: 'Hetzner CCX23',          providerSlug: 'hetzner',  nameTemplate: 'vpn-us-east-{N}' },
  { id: 'us-west', purpose: 'general', country: 'US', city: 'Hillsboro',  cityDefault: 'Hillsboro',   continent: 'North America', label: 'US West (Hillsboro)',     providerHint: 'Hetzner CCX23',          providerSlug: 'hetzner',  nameTemplate: 'vpn-us-west-{N}' },
  { id: 'uk',      purpose: 'general', country: 'GB',                     cityDefault: 'London',      continent: 'Europe',        label: 'UK (London)',             providerHint: 'OVH VPS Comfort',        providerSlug: 'ovh',      nameTemplate: 'vpn-uk-{N}' },
  { id: 'de',      purpose: 'general', country: 'DE',                     cityDefault: 'Frankfurt',   continent: 'Europe',        label: 'Germany (Frankfurt)',     providerHint: 'Hetzner CCX23',          providerSlug: 'hetzner',  nameTemplate: 'vpn-de-{N}' },
  { id: 'nl',      purpose: 'general', country: 'NL',                     cityDefault: 'Amsterdam',   continent: 'Europe',        label: 'Netherlands (Amsterdam)', providerHint: 'OVH VPS Comfort',        providerSlug: 'ovh',      nameTemplate: 'vpn-nl-{N}' },
  { id: 'fr',      purpose: 'general', country: 'FR',                     cityDefault: 'Paris',       continent: 'Europe',        label: 'France (Paris)',          providerHint: 'OVH VPS Comfort',        providerSlug: 'ovh',      nameTemplate: 'vpn-fr-{N}' },
  { id: 'fi',      purpose: 'general', country: 'FI',                     cityDefault: 'Helsinki',    continent: 'Europe',        label: 'Finland (Helsinki)',      providerHint: 'Hetzner CCX23',          providerSlug: 'hetzner',  nameTemplate: 'vpn-fi-{N}' },
  { id: 'sg',      purpose: 'general', country: 'SG',                     cityDefault: 'Singapore',   continent: 'Asia',          label: 'Singapore',               providerHint: 'Hetzner CCX23',          providerSlug: 'hetzner',  nameTemplate: 'vpn-sg-{N}' },
  { id: 'jp',      purpose: 'general', country: 'JP',                     cityDefault: 'Tokyo',       continent: 'Asia',          label: 'Japan (Tokyo)',           providerHint: 'Vultr High-Frequency',   providerSlug: 'vultr',    nameTemplate: 'vpn-jp-{N}' },
  { id: 'ca',      purpose: 'general', country: 'CA',                     cityDefault: 'Toronto',     continent: 'North America', label: 'Canada (Toronto)',        providerHint: 'OVH VPS Comfort',        providerSlug: 'ovh',      nameTemplate: 'vpn-ca-{N}' },
  { id: 'au',      purpose: 'general', country: 'AU',                     cityDefault: 'Sydney',      continent: 'Oceania',       label: 'Australia (Sydney)',      providerHint: 'OVH VPS Comfort',        providerSlug: 'ovh',      nameTemplate: 'vpn-au-{N}' },
  { id: 'br',      purpose: 'general', country: 'BR',                     cityDefault: 'São Paulo',   continent: 'South America', label: 'Brazil (São Paulo)',      providerHint: 'Vultr High-Frequency',   providerSlug: 'vultr',    nameTemplate: 'vpn-br-{N}' },
  { id: 'ae',      purpose: 'general', country: 'AE',                     cityDefault: 'Dubai',       continent: 'Asia',          label: 'UAE (Dubai)',             providerHint: 'Vultr Regular',          providerSlug: 'vultr',    nameTemplate: 'vpn-ae-{N}' },
  { id: 'in',      purpose: 'general', country: 'IN',                     cityDefault: 'Mumbai',      continent: 'Asia',          label: 'India (Mumbai)',          providerHint: 'Vultr Regular',          providerSlug: 'vultr',    nameTemplate: 'vpn-in-{N}' },

  // ── P2P exits — 3 boxes in DMCA-friendly jurisdictions ───────────────
  { id: 'p2p-lu', purpose: 'p2p', requiredCapability: 'p2p', country: 'LU', cityDefault: 'Luxembourg', continent: 'Europe',   label: 'P2P · Luxembourg',  providerHint: 'BuyVM KVM Slice',  providerSlug: 'buyvm',     nameTemplate: 'p2p-lu-{N}' },
  { id: 'p2p-nl', purpose: 'p2p', requiredCapability: 'p2p', country: 'NL', cityDefault: 'Amsterdam',  continent: 'Europe',   label: 'P2P · Amsterdam',   providerHint: 'HostHatch NVMe',   providerSlug: 'hosthatch', nameTemplate: 'p2p-nl-{N}' },
  { id: 'p2p-hk', purpose: 'p2p', requiredCapability: 'p2p', country: 'HK', cityDefault: 'Hong Kong',  continent: 'Asia',     label: 'P2P · Hong Kong',   providerHint: 'HostHatch NVMe',   providerSlug: 'hosthatch', nameTemplate: 'p2p-hk-{N}' },

  // ── Tor exit — 1 box in a Tor-tolerant jurisdiction ──────────────────
  { id: 'tor-ro', purpose: 'tor', requiredCapability: 'tor', country: 'RO', cityDefault: 'Bucharest',  continent: 'Europe',   label: 'Tor · Romania',     providerHint: 'FlokiNET VPS-X',   providerSlug: 'flokinet',  nameTemplate: 'tor-ro-{N}' },

  // ── Mixnet — Nym gateways in privacy-friendly jurisdictions ──────────
  // Mixnet capability is wired into the routing-side selector (#77) and the
  // capability ENUM, so a registered + provisioned mixnet box is selectable
  // at session-mint time.
  { id: 'mix-ch', purpose: 'mixnet', requiredCapability: 'mixnet', country: 'CH', cityDefault: 'Zurich',    continent: 'Europe', label: 'Mixnet · Switzerland', providerHint: 'Privex / FlokiNET', providerSlug: 'flokinet',  nameTemplate: 'mix-ch-{N}' },
  { id: 'mix-is', purpose: 'mixnet', requiredCapability: 'mixnet', country: 'IS', cityDefault: 'Reykjavík', continent: 'Europe', label: 'Mixnet · Iceland',     providerHint: 'FlokiNET',         providerSlug: 'flokinet',  nameTemplate: 'mix-is-{N}' },
  { id: 'mix-nl', purpose: 'mixnet', requiredCapability: 'mixnet', country: 'NL', cityDefault: 'Amsterdam', continent: 'Europe', label: 'Mixnet · Amsterdam',   providerHint: 'HostHatch NVMe',   providerSlug: 'hosthatch', nameTemplate: 'mix-nl-{N}' },
  { id: 'mix-sg', purpose: 'mixnet', requiredCapability: 'mixnet', country: 'SG', cityDefault: 'Singapore', continent: 'Asia',   label: 'Mixnet · Singapore',   providerHint: 'HostHatch',        providerSlug: 'hosthatch', nameTemplate: 'mix-sg-{N}' },
];

// Back-compat alias for any caller that still imports PLANNED_REGIONS.
export const PLANNED_REGIONS = PLANNED_EXITS;
export type PlannedRegion = PlannedExit;

/**
 * Suggest the next server name for a planned exit given what's already
 * deployed in the same tier+region. e.g. existing vpn-de-01, vpn-de-02 →
 * suggests vpn-de-03.
 */
export function suggestServerName(plan: PlannedExit, existing: ServerListItem[]): string {
  const next = existing.length + 1;
  return plan.nameTemplate.replace('{N}', String(next).padStart(2, '0'));
}

/**
 * How many active nodes a region needs to count as fully covered. Mixnet runs a
 * 3-layer Nym cascade, so a mixnet region targets 3 nodes; the other tiers need
 * one. Tune per tier here.
 */
export const TARGET_NODES_BY_TIER: Record<PurposeTier, number> = {
  general: 1,
  p2p: 1,
  tor: 1,
  mixnet: 3,
};

export interface ExitCoverageEntry {
  plan: PlannedExit;
  /** Matching servers (may be more than one — tier slot might be doubled-up). */
  matches: ServerListItem[];
  /** Active nodes this region needs to be fully covered (per its tier). */
  target: number;
  /** True once `matches.length` reaches `target`. */
  covered: boolean;
}

/**
 * Pure function: given a server list, return a coverage entry per planned
 * exit. Match logic:
 *
 * - **General**: ACTIVE + role in {exit_node, vpn_server} + country
 *   (case-insensitive) matches + optional city substring matches.
 * - **P2P / Tor / Mixnet**: same status+role gate, then must advertise
 *   the required `capability` AND match the country. (Country still
 *   matters: a P2P box in Romania doesn't satisfy the P2P-Amsterdam
 *   slot.)
 */
export function computeExitCoverage(servers: ServerListItem[]): ExitCoverageEntry[] {
  const exits = servers.filter(
    (s) =>
      s.status === 'active' &&
      (s.role === 'exit_node' || s.role === 'vpn_server'),
  );

  return PLANNED_EXITS.map((plan) => {
    const matches = exits.filter((s) => {
      // Defensive against half-registered servers with NULL region fields.
      const country = s.region?.country?.toUpperCase();
      if (!country || country !== plan.country.toUpperCase()) return false;
      if (plan.city) {
        const city = s.region?.city?.toLowerCase();
        if (!city || !city.includes(plan.city.toLowerCase())) return false;
      }
      if (plan.requiredCapability) {
        const caps = s.capabilities ?? [];
        if (!caps.includes(plan.requiredCapability)) return false;
      }
      return true;
    });
    const target = TARGET_NODES_BY_TIER[plan.purpose];
    return { plan, matches, target, covered: matches.length >= target };
  });
}

// Back-compat alias for any caller that still imports computeRegionCoverage.
export const computeRegionCoverage = computeExitCoverage;
export type RegionCoverageEntry = ExitCoverageEntry;
